/**
 * Which backend this page talks to (wiki spec--bimpdf-local-serve): Firebase,
 * or the LOCAL REVIEW SERVER behind the dev server's /local proxy. Chosen ONCE
 * at boot (switching reloads the page -- services are never swapped live):
 *   1. a join link's `b=local | b=firebase` (the /join fragment) -- remembered;
 *   2. else what this origin remembered;
 *   3. else VITE_BACKEND (the dev server started by `bim pdf serve --local`).
 * `local` is honoured only where a local server can exist: a dev build, or a
 * build with VITE_ALLOW_LOCAL_BACKEND=1. A production build is always Firebase.
 */
export type Backend = 'firebase' | 'local';

export const BACKEND_KEY = 'pmt.backend';

interface Env {
    DEV?: boolean;
    VITE_BACKEND?: string;
    VITE_ALLOW_LOCAL_BACKEND?: string;
}

export interface ResolveInput {
    pathname: string;
    hash: string;
    stored: string | null;
    env: Env;
}

export function localBackendAllowed(env: Env): boolean {
    return !!env.DEV || env.VITE_ALLOW_LOCAL_BACKEND === '1';
}

/** The backend + what to remember (null = leave storage alone). Pure. */
export function resolveBackend({ pathname, hash, stored, env }: ResolveInput): { backend: Backend; remember: Backend | null } {
    if (!localBackendAllowed(env)) return { backend: 'firebase', remember: null };
    const asked = pathname.replace(/\/+$/, '') === '/join' ? new URLSearchParams(hash.replace(/^#/, '')).get('b') : null;
    if (asked === 'local' || asked === 'firebase') return { backend: asked, remember: asked };
    if (stored === 'local' || stored === 'firebase') return { backend: stored, remember: null };
    return { backend: env.VITE_BACKEND === 'local' ? 'local' : 'firebase', remember: null };
}

let current: Backend = 'firebase';

/** Resolve for this page load (browser), remembering a join link's choice. */
export function bootBackend(): Backend {
    let stored: string | null = null;
    try {
        stored = localStorage.getItem(BACKEND_KEY);
    } catch {
        // storage blocked: no memory, the link / env still decide
    }
    const { backend, remember } = resolveBackend({ pathname: location.pathname, hash: location.hash, stored, env: import.meta.env as Env });
    if (remember) {
        try {
            localStorage.setItem(BACKEND_KEY, remember);
        } catch {
            // ignored
        }
    }
    current = backend;
    return backend;
}

/**
 * Is this page a LOCAL review session -- answerable BEFORE bootBackend runs (analytics boots
 * first, at App load)? Remote analytics/telemetry stay OFF on such a page: the pairing page
 * promises the iPad that the session's drawings and markups stay on this network (local
 * README "iPad"; network audit 2026-10-01). Never true in a production build (localBackendAllowed).
 */
export function isLocalBackendPage(): boolean {
    if (current === 'local') return true;
    if (typeof location === 'undefined') return false;
    let stored: string | null = null;
    try {
        stored = localStorage.getItem(BACKEND_KEY);
    } catch {
        // storage blocked: the link / env still decide
    }
    return resolveBackend({ pathname: location.pathname, hash: location.hash, stored, env: import.meta.env as Env }).backend === 'local';
}

/** The backend this page booted with (firebase until bootBackend runs). */
export function currentBackend(): Backend {
    return current;
}

/** Switch backends: remember the choice and reload (services are never swapped in place). */
export function switchBackend(to: Backend): void {
    try {
        localStorage.setItem(BACKEND_KEY, to);
    } catch {
        // ignored: the reload then falls back to the env default
    }
    location.reload();
}

/**
 * Leave a LOCAL review session for pdfmarkuptool.com's backend. The viewer's overflow menu offers
 * it (UI-FINAL U1: the fixed LOCAL badge on the work surface became a menu item + a text tag);
 * asks first, because the page reloads and the local session is left behind.
 */
export function leaveLocalBackend(): void {
    if (window.confirm('Leave the local review session and use the Firebase backend? The page reloads.')) switchBackend('firebase');
}
